/**
 * Theme Utilities Module
 *
 * Provides utilities for converting tenant colors to CSS custom properties
 * and applying themes throughout the application.
 *
 * Note: We use --theme-* prefix for CSS variables to avoid conflicts with
 * the existing shadcn HSL-based --primary, --secondary, --accent variables.
 *
 * @module theme-utils
 */

/**
 * Default color values used when tenant colors are not configured.
 * These match the Prisma schema defaults and are used as fallbacks.
 */
export const DEFAULT_COLORS = {
  primary: '#3B82F6',    // Blue - matches Tenant.primaryColor default
  secondary: '#1E40AF',  // Darker blue - matches Tenant.secondaryColor default
  accent: '#10B981',     // Emerald green - matches Tenant.accentColor default
  bg: '#FFFFFF',         // White - matches Tenant.backgroundColor default
  text: '#111827',       // Dark gray - matches Tenant.textColor default
  'footer-bg': '#111827', // Dark gray - matches Tenant.footerBackgroundColor default
} as const;

/**
 * Type for default color keys
 */
export type DefaultColorKey = keyof typeof DEFAULT_COLORS;

/**
 * Mapping from tenant color field names to CSS custom property names.
 * This maps the database field names to the CSS variable names used in stylesheets.
 * Uses --theme-* prefix to avoid conflicts with shadcn's HSL-based variables.
 */
export const COLOR_MAPPING = {
  primaryColor: '--theme-primary',
  secondaryColor: '--theme-secondary',
  accentColor: '--theme-accent',
  backgroundColor: '--theme-bg',
  textColor: '--theme-text',
  footerBackgroundColor: '--theme-footer-bg',
} as const;

/**
 * Type for tenant color field names
 */
export type TenantColorField = keyof typeof COLOR_MAPPING;

/**
 * Type for CSS variable names
 */
export type CSSVariableName = (typeof COLOR_MAPPING)[TenantColorField];

/**
 * Interface for tenant color configuration.
 * All fields are optional as tenants may have partial color configurations.
 */
export interface TenantColors {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  accentColor?: string | null;
  backgroundColor?: string | null;
  textColor?: string | null;
  footerBackgroundColor?: string | null;
}

/**
 * Type for the CSS variables object returned by generateCSSVariables
 */
export type CSSVariablesObject = Record<CSSVariableName, string>;

/**
 * Returns a color value with a fallback to default if the value is null, undefined, or empty.
 *
 * @param color - The color value to check
 * @param defaultValue - The default value to use if color is not set
 * @returns The color value or the default
 *
 * @example
 * ```typescript
 * getColorWithDefault('#FF0000', '#3B82F6'); // Returns '#FF0000'
 * getColorWithDefault(null, '#3B82F6');      // Returns '#3B82F6'
 * getColorWithDefault('', '#3B82F6');        // Returns '#3B82F6'
 * ```
 */
export function getColorWithDefault(
  color: string | null | undefined,
  defaultValue: string
): string {
  if (!color || color.trim() === '') {
    return defaultValue;
  }
  return color;
}

/**
 * Generates an object of CSS custom properties from tenant colors.
 * Uses default values for any colors that are not set.
 *
 * @param tenant - The tenant object with color configuration (can be null/undefined)
 * @returns An object with CSS custom property names as keys and color values
 *
 * @example
 * ```typescript
 * const tenant = {
 *   primaryColor: '#FF0000',
 *   secondaryColor: '#00FF00',
 * };
 *
 * const cssVars = generateCSSVariables(tenant);
 * // Returns:
 * // {
 * //   '--theme-primary': '#FF0000',
 * //   '--theme-secondary': '#00FF00',
 * //   '--theme-accent': '#10B981',      // default
 * //   '--theme-bg': '#FFFFFF',          // default
 * //   '--theme-text': '#111827',        // default
 * //   '--theme-footer-bg': '#111827',   // default
 * // }
 * ```
 */
export function generateCSSVariables(
  tenant: TenantColors | null | undefined
): CSSVariablesObject {
  return {
    '--theme-primary': getColorWithDefault(tenant?.primaryColor, DEFAULT_COLORS.primary),
    '--theme-secondary': getColorWithDefault(tenant?.secondaryColor, DEFAULT_COLORS.secondary),
    '--theme-accent': getColorWithDefault(tenant?.accentColor, DEFAULT_COLORS.accent),
    '--theme-bg': getColorWithDefault(tenant?.backgroundColor, DEFAULT_COLORS.bg),
    '--theme-text': getColorWithDefault(tenant?.textColor, DEFAULT_COLORS.text),
    '--theme-footer-bg': getColorWithDefault(tenant?.footerBackgroundColor, DEFAULT_COLORS['footer-bg']),
  };
}

/**
 * Applies CSS variables to the document root element.
 * This function should only be called on the client side.
 *
 * @param cssVariables - Object of CSS variable names and values to apply
 *
 * @example
 * ```typescript
 * const cssVars = generateCSSVariables(tenant);
 * applyCSSVariablesToRoot(cssVars);
 * ```
 */
export function applyCSSVariablesToRoot(cssVariables: CSSVariablesObject): void {
  if (typeof document === 'undefined') {
    return; // Server-side, no document available
  }

  const root = document.documentElement;

  for (const [property, value] of Object.entries(cssVariables)) {
    root.style.setProperty(property, value);
  }
}

/**
 * Removes CSS variables from the document root element.
 * This function should only be called on the client side.
 *
 * @param variableNames - Array of CSS variable names to remove
 *
 * @example
 * ```typescript
 * removeCSSVariablesFromRoot(['--theme-primary', '--theme-secondary']);
 * ```
 */
export function removeCSSVariablesFromRoot(variableNames: CSSVariableName[]): void {
  if (typeof document === 'undefined') {
    return; // Server-side, no document available
  }

  const root = document.documentElement;

  for (const property of variableNames) {
    root.style.removeProperty(property);
  }
}

/**
 * Gets all CSS variable names defined in this module.
 *
 * @returns Array of CSS variable names
 */
export function getAllCSSVariableNames(): CSSVariableName[] {
  return Object.values(COLOR_MAPPING);
}
